/**
 * Frontend mirror of backend `CandidateDetailsResponse`.
 *
 * Kept in sync by hand with backend/src/types/candidateDetails.ts. Changes to
 * either file require an equivalent edit in the other.
 */

export type SeniorityLevel = 'intern' | 'junior' | 'mid' | 'senior' | 'lead' | 'principal';
export type AvailabilityStatus = 'available' | 'open' | 'not_looking';
export type ReviewStatus = 'unreviewed' | 'needs_review' | 'reviewed';

export interface CandidateAvailability {
  status: AvailabilityStatus | string | null;
  date: string | null;
  noticePeriodWeeks: number | null;
}

export interface CandidateRate {
  country: string | null;
  expectationMin: number | null;
  expectationMax: number | null;
  currency: string | null;
}

export interface CandidateExperienceEntry {
  id: number;
  companyName: string | null;
  roleTitle: string | null;
  employmentType: string | null;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
  location: string | null;
  locationModel: string | null;
  summary: string | null;
  responsibilities: string[];
  achievements: string[];
  teamContext: string | null;
  senioritySignal: string | null;
  managementSignal: string | null;
  orderIndex: number | null;
  source: string;
  confidence: number;
}

export interface CandidateEducationEntry {
  id: number;
  institution: string | null;
  degree: string | null;
  fieldOfStudy: string | null;
  startDate: string | null;
  endDate: string | null;
  grade: string | null;
  summary: string | null;
  source: string;
  confidence: number;
}

export interface CandidateCertificationEntry {
  id: number;
  name: string;
  issuer: string | null;
  issuedDate: string | null;
  expiresDate: string | null;
  credentialId: string | null;
  credentialUrl: string | null;
  source: string;
  confidence: number;
}

export interface CandidateLanguageEntry {
  language: string;
  proficiency: string | null;
  source: string;
}

export interface CandidateDomainEntry {
  domain: string;
  yearsExperience: number | null;
  source: string;
}

/**
 * Phase 3E-3 — rich per-skill evidence. `skillName` is resolved from the
 * canonical `skills` table on the backend; if the ontology has no entry for
 * a given `skillId` (rare, legacy rows only), `skillName` is `null` and the
 * UI should fall back to `skillId`.
 */
export interface CandidateSkillEntry {
  skillId: string;
  skillName: string | null;
  role: string | null;
  proficiency: string | null;
  yearsExperience: number | null;
  lastUsedYear: number | null;
  confidence: number;
  source: string;
}

export interface CandidateSourceMetadata {
  poolId: number | null;
  originalFilename: string | null;
  parsedAt: string | null;
}

/**
 * Phase 3E-2 — raw CV text layer returned by
 * `GET /api/people/:id/source-layers`. Mirrors `CvTextLayer` on the backend.
 */
export interface CvTextLayer {
  sourceKind: string | null;
  fileName: string | null;
  mimeType: string | null;
  contentSha256: string | null;
  rawText: string | null;
  textLength: number | null;
  parserVersion: string | null;
  languageDetected: string | null;
  importedBy: string | null;
  importedAt: string | null;
}

/**
 * A single shadow-layer snapshot of a candidate's evidence collections. Both
 * `cv_parse` (parser-owned rows) and `candidate_input` (self-service rows)
 * project through this same shape; the frontend renders them with the same
 * read-only row components as `Current`.
 *
 * Phase 3E-3 added skills/languages/domains so every editable tab can offer
 * the same Current / CV parse / Candidate input view selector.
 */
export interface CandidateEvidenceLayer {
  experiences: CandidateExperienceEntry[];
  education: CandidateEducationEntry[];
  certifications: CandidateCertificationEntry[];
  skills: CandidateSkillEntry[];
  languages: CandidateLanguageEntry[];
  domains: CandidateDomainEntry[];
}

/**
 * Response of `GET /api/people/:id/source-layers`. The `current` projected
 * layer is intentionally omitted — callers already have it via
 * `GET /api/people/:id`'s `candidateDetails` field.
 */
export interface CandidateSourceLayersResponse {
  personId: number;
  layers: {
    cv_text: CvTextLayer | null;
    cv_parse: CandidateEvidenceLayer;
    candidate_input: CandidateEvidenceLayer;
  };
}

/**
 * Phase 3E-2 — snake_case request shape for
 * `PUT /api/people/:id/experience`. Mirrors `ExperienceEntryInput` on the
 * backend so we don't have to re-case on serialization.
 *
 * Omitting a field is equivalent to `null`; the server trims strings and
 * drops empty bullets.
 */
export interface ExperienceProjectInput {
  project_name?: string | null;
  client_or_product?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  summary?: string | null;
  responsibilities?: string[] | null;
  outcomes?: string[] | null;
  tools?: string[] | null;
}

export interface ExperienceEntryInput {
  company_name?: string | null;
  role_title?: string | null;
  employment_type?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_current?: boolean | null;
  location?: string | null;
  location_model?: string | null;
  summary?: string | null;
  responsibilities?: string[] | null;
  achievements?: string[] | null;
  team_context?: string | null;
  seniority_signal?: string | null;
  management_signal?: string | null;
  projects?: ExperienceProjectInput[] | null;
}

/**
 * 400 / 413 response shape for `PUT /api/people/:id/experience`. `field` is
 * a dotted path like `entries[3].projects[1].tools` so the editor modal can
 * highlight the offending control.
 */
export interface ExperiencePutErrorResponse {
  error: string;
  field?: string;
  allowed?: string[];
  limit?: number;
}

/**
 * Phase 3E-3 — SKILL_ROLES mirrors the backend vocabulary in
 * backend/src/types/candidate.ts. Keep in sync.
 */
export const SKILL_ROLES = [
  'primary',
  'secondary',
  'tool',
  'language',
  'domain',
] as const;
export type SkillRole = (typeof SKILL_ROLES)[number];

/**
 * Phase 3E-3 — SKILL_PROFICIENCIES mirrors
 * `SKILL_PROFICIENCIES` in backend/src/services/peopleService.ts.
 */
export const SKILL_PROFICIENCIES = [
  'learning',
  'working',
  'proficient',
  'expert',
] as const;
export type SkillProficiency = (typeof SKILL_PROFICIENCIES)[number];

/**
 * Phase 3E-3 — snake_case request shape for
 * `PUT /api/people/:id/skills`. Mirrors `SkillInput` on the backend.
 */
export interface SkillInput {
  skill_id?: string | null;
  skill_role?: string | null;
  proficiency?: string | null;
  years_experience?: number | null;
  last_used_year?: number | null;
}

/** Request shape for `PUT /api/people/:id/domains`. */
export interface DomainInput {
  domain?: string | null;
  years_experience?: number | null;
}

/** Matches backend `LanguageProficiency` / PUT /languages validation. */
export const LANGUAGE_PROFICIENCIES = [
  'basic',
  'working',
  'professional',
  'fluent',
  'native',
] as const;
export type LanguageProficiencyLevel = (typeof LANGUAGE_PROFICIENCIES)[number];

/** Request shape for `PUT /api/people/:id/languages`. */
export interface LanguageInput {
  language?: string | null;
  proficiency?: string | null;
}

/** Request shape for `PUT /api/people/:id/education`. */
export interface EducationInput {
  institution?: string | null;
  degree?: string | null;
  field_of_study?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  grade?: string | null;
  summary?: string | null;
}

/** Request shape for `PUT /api/people/:id/certifications`. */
export interface CertificationInput {
  name?: string | null;
  issuer?: string | null;
  issued_date?: string | null;
  expires_date?: string | null;
  credential_id?: string | null;
  credential_url?: string | null;
}

export interface CandidateDetailsResponse {
  professionalSummary: string | null;
  seniorityLevel: SeniorityLevel | string | null;
  educationLevel: string | null;
  yearsExperienceTotal: number | null;
  availability: CandidateAvailability;
  rate: CandidateRate;
  locationModelPreferences: string[];
  employmentTypePreferences: string[];
  workAuthorizationRegions: string[];
  reviewStatus: ReviewStatus;
  reviewNotes: string | null;
  /** ISO timestamp of the most recent review-status change to `reviewed`. */
  reviewedAt: string | null;
  /** Email of the reviewer who last set status to `reviewed`. */
  reviewedBy: string | null;
  parserVersion: string | null;
  parseConfidence: Record<string, number> | null;
  experiences: CandidateExperienceEntry[];
  education: CandidateEducationEntry[];
  certifications: CandidateCertificationEntry[];
  /**
   * Phase 3E-3 — rich per-skill evidence with role / proficiency / source
   * after shadow-layer precedence (self_service > admin_edit > cv). The
   * legacy `Person.skills: string[]` is still populated for search, chat and
   * filter chips; this richer array feeds the Skills tab + editor.
   */
  skills: CandidateSkillEntry[];
  languages: CandidateLanguageEntry[];
  targetRoles: string[];
  domains: CandidateDomainEntry[];
  sourceMetadata: CandidateSourceMetadata;
}
